import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Boxes, ClipboardCheck, Wallet, MoreHorizontal, Receipt, ArrowLeftRight, Undo2, History, User, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate } from "@/lib/format";

const LOCK_LABEL = { LEAVE: "Leave", ABSENT: "Absent", HOLIDAY: "Holiday" };

function LockedNotice({ lock, onLogout }) {
  return (
    <div className="space-y-4 rounded-md border border-amber-300 bg-amber-50 p-5" data-testid="day-locked-notice">
      <p className="text-lg font-bold text-amber-900">Day closed</p>
      <p className="text-sm text-amber-900" data-testid="day-locked-message">Your day is closed because your attendance is marked as {LOCK_LABEL[lock.status] || lock.status}. Please contact Admin if this is incorrect.</p>
      <div className="grid grid-cols-2 gap-2 text-sm"><p>Attendance<br /><b data-testid="day-locked-status">{LOCK_LABEL[lock.status] || lock.status}</b></p><p>Business date<br /><b data-testid="day-locked-date">{fmtDate(lock.date)}</b></p></div>
      <button onClick={onLogout} className="w-full rounded-md border border-red-300 bg-white py-3 text-sm font-semibold text-red-600" data-testid="day-locked-logout">Log out</button>
    </div>
  );
}

const MAIN = [["", "Today", Home], ["stock", "My Stock", Boxes], ["close", "Close Day", ClipboardCheck], ["collections", "Collections", Wallet]];
const MORE = [["expenses", "Expenses", Receipt], ["transfers", "Transfers", ArrowLeftRight], ["returns", "Returns", Undo2], ["history", "History", History], ["profile", "Profile", User]];

export default function SalesmanLayout() {
  const { salesman, bizDate, logout } = useAuth();
  const [more, setMore] = useState(false);
  const nav = useNavigate();
  const go = (p) => { setMore(false); nav(`/s/${p}`); };
  const { data: lock } = useFetch("/attendance/my-lock");
  const logoutNow = () => { logout(); nav("/login"); };
  if (lock?.locked) return <div className="min-h-screen bg-slate-50"><main className="mx-auto max-w-xl px-4 py-10"><p className="mb-4 text-sm font-bold" data-testid="salesman-name">{salesman?.full_name}</p><LockedNotice lock={lock} onLogout={logoutNow} /></main></div>;
  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-slate-900 font-mono text-sm font-bold text-white">S</div>
            <div><p className="text-sm font-bold leading-tight" data-testid="salesman-name">{salesman?.full_name}</p><p className="text-[11px] text-slate-500">{salesman?.code}</p></div>
          </div>
          <div className="text-right"><p className="text-[10px] uppercase tracking-wider text-slate-500">Business day</p><p className="text-sm font-semibold" data-testid="salesman-biz-date">{fmtDate(bizDate)}</p></div>
        </div>
      </header>
      <main className="mx-auto max-w-xl px-4 py-5"><Outlet /></main>
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-xl grid-cols-5">
          {MAIN.map(([p, l, Icon]) => (
            <NavLink key={p} to={`/s/${p}`} end={p === ""} data-testid={`snav-${p || "today"}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-3 text-[11px] font-semibold ${isActive ? "text-blue-600" : "text-slate-500"}`}>
              <Icon className="h-5 w-5" /> {l}
            </NavLink>
          ))}
          <button onClick={() => setMore(true)} className="flex flex-col items-center gap-1 py-3 text-[11px] font-semibold text-slate-500" data-testid="snav-more">
            <MoreHorizontal className="h-5 w-5" /> More
          </button>
        </div>
      </nav>
      <Sheet open={more} onOpenChange={setMore}>
        <SheetContent side="bottom" className="rounded-t-xl">
          <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {MORE.map(([p, l, Icon]) => (
              <button key={p} onClick={() => go(p)} data-testid={`snav-${p}`} className="flex flex-col items-center gap-2 rounded-md border border-slate-200 py-4 text-sm font-semibold text-slate-700 active:bg-slate-100">
                <Icon className="h-6 w-6" /> {l}
              </button>
            ))}
            <button onClick={() => { logout(); nav("/login"); }} data-testid="salesman-logout" className="flex flex-col items-center gap-2 rounded-md border border-red-200 py-4 text-sm font-semibold text-red-600">
              <LogOut className="h-6 w-6" /> Log out
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
